import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

const RicambiSelector = ({ onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [ricambiList, setRicambiList] = useState([]);
  const [filteredRicambi, setFilteredRicambi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch all ricambi on component mount
  useEffect(() => {
    fetchRicambi();
  }, []);

  // Filter ricambi based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRicambi(ricambiList);
    } else {
      const filtered = ricambiList.filter(item =>
        item.Item.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRicambi(filtered);
    }
  }, [searchTerm, ricambiList]);

  const fetchRicambi = async () => {
    try {
      setLoading(true);
      const response = await api.getRicambi();
      setRicambiList(response.ricambi || []);
      setFilteredRicambi(response.ricambi || []);
    } catch (error) {
      console.error('Error fetching ricambi:', error);
      setRicambiList([]);
      setFilteredRicambi([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    onSelect({
      ricambioCode: item.Item,
      description: item.Description
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="px-2 sm:px-4">
      <h1 className="text-xl font-bold text-center mb-6">
        SCEGLI RICAMBIO
      </h1>

      {/* Search field */}
      <div className="mb-6">
        <div className="relative max-w-md mx-auto">
          <input
            type="text"
            placeholder="Cerca per codice o descrizione..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
          />
          <svg
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-red-500"></div>
          <p className="mt-4 text-gray-600 font-medium">Caricamento ricambi...</p>
        </div>
      )}

      {/* Ricambi grid - no internal scrolling, let page scroll */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
          {filteredRicambi.length > 0 ? (
            filteredRicambi.map((item) => (
              <button
                key={item.Item}
                onClick={() => handleItemSelect(item)}
                className={`
                  p-3 sm:p-4 lg:p-6 bg-white rounded-xl shadow hover:shadow-lg transition-all duration-200 
                  flex flex-col justify-between min-h-[140px] sm:min-h-[180px] lg:min-h-[220px]
                  hover:scale-105 active:scale-95
                  ${selectedItem?.Item === item.Item ? 'ring-2 ring-red-500' : ''}
                `}
              >
                <div className="flex-1 flex flex-col items-center justify-center text-center px-1 mb-2">
                  <p className="text-xs sm:text-sm lg:text-base text-gray-700 break-words whitespace-normal leading-snug">
                    {item.Description}
                  </p>
                </div>
                <div className="flex justify-center items-center h-8 sm:h-9 lg:h-10 border border-gray-300 rounded w-full">
                  <span className="font-medium text-sm sm:text-base lg:text-lg px-2 truncate" title={item.Item}>
                    {item.Item}
                  </span>
                </div>
              </button>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="bg-gray-50 rounded-xl p-8 max-w-md mx-auto">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2C4.477 2 0 6.477 0 12s4.477 10 10 10 10-4.477 10-10S15.523 2 10 2zM9 7a1 1 0 112 0v4a1 1 0 11-2 0V7zm1 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <p className="text-gray-500 font-medium">
                  {searchTerm ? 'Nessun ricambio trovato per la ricerca' : 'Nessun ricambio disponibile'}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-3 text-red-500 hover:text-red-700 font-medium"
                  >
                    Cancella ricerca
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show selected ricambio description below grid */}
      {selectedItem && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-center">
            <p className="text-sm text-red-600 font-medium mb-1">Ricambio selezionato:</p>
            <p className="text-red-800 font-semibold">{selectedItem.Item}</p>
            <p className="text-red-700 text-sm">{selectedItem.Description}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RicambiSelector;